import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useForm, ValidationError } from "@formspree/react";

const Contact = () => {
  const [state, handleSubmit] = useForm("mkgvykba");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "General Inquiry",
    message: "",
  });

  // Clear form when submission succeeds
  React.useEffect(() => {
    if (state.succeeded) {
      setFormData({
        name: "",
        email: "",
        subject: "General Inquiry",
        message: "",
      });
    }
  }, [state.succeeded]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="max-w-4xl w-full">
      {/* Success Message */}
      {state.succeeded && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-gradient-to-r from-green-500 to-blue-500 text-white px-8 py-4 rounded-xl shadow-lg border border-green-400/30 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <span className="text-2xl">✅</span>
            <div>
              <p className="font-bold text-lg">Message Sent Successfully!</p>
              <p className="text-sm opacity-90">
                We'll get back to you within 24-48 hours.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Instagram Notice */}
      <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-2 border-yellow-400/50 rounded-2xl p-6 mb-8 backdrop-blur-sm">
        <div className="text-center">
          <div className="text-3xl mb-3">📱</div>
          <h3 className="text-yellow-400 font-bold text-xl mb-3">
            Important Notice
          </h3>
          <p className="text-white mb-2 text-sm">
            For inquiries about this site, or if you'd like me to make you your
            own site, please DM me on Instagram at{" "}
            <span className="text-yellow-400 font-semibold">@tqrqn</span>.
          </p>
          <p className="text-gray-300 text-sm">
            Note: none of the other contact links on this site are active. This
            is the only way to reach me.
          </p>
        </div>
      </div>

      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-5xl font-bold text-white mb-4 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
          Contact Us
        </h1>
        <p className="text-gray-300 text-xl max-w-2xl mx-auto">
          We're here to help kids fighting cancer. Reach out to us for support,
          questions, or to learn how you can make a difference.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Contact Info Cards */}
        <div className="lg:col-span-1 space-y-6">
          {/* Email Card */}
          <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-sm rounded-2xl p-6 border border-blue-400/30 hover:border-blue-400/50 transition-all">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-blue-500/30 rounded-xl flex items-center justify-center mr-4">
                <span className="text-2xl">📧</span>
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Email Us</h3>
                <p className="text-gray-300 text-sm">Direct contact</p>
              </div>
            </div>
            <a
              href="mailto:contact@maxwinsferdakids.org"
              className="text-blue-400 hover:text-blue-300 font-mono text-sm transition-colors whitespace-nowrap"
            >
              contact@maxwinsferdakids.org
            </a>
            <p className="text-gray-400 text-sm mt-2">
              ⚡ Response within 24-48 hours
            </p>
          </div>

          {/* Mission Card */}
          <div className="bg-gradient-to-br from-green-500/20 to-blue-500/20 backdrop-blur-sm rounded-2xl p-6 border border-green-400/30 hover:border-green-400/50 transition-all">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-green-500/30 rounded-xl flex items-center justify-center mr-4">
                <span className="text-2xl">💜</span>
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Our Mission</h3>
                <p className="text-gray-300 text-sm">Why we exist</p>
              </div>
            </div>
            <p className="text-gray-300 leading-relaxed">
              Supporting children with cancer and their families through max win
              donations and direct financial assistance.
            </p>
          </div>

          {/* Quick Help Card */}
          <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-sm rounded-2xl p-6 border border-purple-400/30 hover:border-purple-400/50 transition-all">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-purple-500/30 rounded-xl flex items-center justify-center mr-4">
                <span className="text-2xl">🛠️</span>
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Quick Help</h3>
                <p className="text-gray-300 text-sm">Common topics</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center text-gray-300">
                <span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>
                <span className="text-sm">Donation questions</span>
              </div>
              <div className="flex items-center text-gray-300">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                <span className="text-sm">Help request assistance</span>
              </div>
              <div className="flex items-center text-gray-300">
                <span className="w-2 h-2 bg-purple-400 rounded-full mr-3"></span>
                <span className="text-sm">Technical support</span>
              </div>
              <div className="flex items-center text-gray-300">
                <span className="w-2 h-2 bg-pink-400 rounded-full mr-3"></span>
                <span className="text-sm">Partnership opportunities</span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="lg:col-span-2">
          <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-sm rounded-2xl p-8 pb-12 border border-gray-600/30">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">
                Send us a Message
              </h2>
              <p className="text-gray-400">
                Fill out the form below and we'll get back to you as soon as
                possible.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/20 transition-all"
                    placeholder="Enter your full name"
                  />
                  <ValidationError
                    prefix="Name"
                    field="name"
                    errors={state.errors}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/20 transition-all"
                    placeholder="your.email@example.com"
                  />
                  <ValidationError
                    prefix="Email"
                    field="email"
                    errors={state.errors}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Subject *
                </label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/20 transition-all"
                >
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Donation Question">Donation Question</option>
                  <option value="Help Request">Help Request</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="Partnership">Partnership Opportunity</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/20 transition-all resize-none"
                  placeholder="Tell us how we can help you or what you'd like to know..."
                ></textarea>
                <ValidationError
                  prefix="Message"
                  field="message"
                  errors={state.errors}
                />
              </div>

              <button
                type="submit"
                disabled={state.submitting}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-blue-500/25 hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>🚀</span>
                {state.submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Back to Home */}
      <div className="text-center mt-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-3 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-lg hover:shadow-purple-500/25"
        >
          <span>🏠</span>
          Back to Home
        </Link>
      </div>
    </div>
  );
};

export default Contact;
